import { Middleware } from '@reduxjs/toolkit';

import { getCookie, QaToolKitOptions } from '@cashier/cashier-common';
import {
  continueProcessing,
  enrichPayloadWithErrors,
  fetchPaymentInformationSuccess,
  fillConfirmationForm,
  initiateConfirmation,
  initiatePaymentSnapshot,
  initiateProcessing,
  processPayment,
  processPaymentFailure,
  processPaymentSuccess,
  refetchCertainPaymentMethod,
  registerPayment,
  registerPaymentFailure,
  registerPaymentSuccess,
  selectPaymentFormPayload,
  setActiveScreen,
  setPaymentFormStatus,
} from '@cashier/cashier-data-access';
import {
  CashierMessages,
  DynamicScreens,
  FormPayload,
  FormStatuses,
  IntegrationSources,
  PaymentActionError,
  PaymentActionResult,
  PaymentStates,
  PaymentTypes,
  PreprocessorTypes,
  RedirectContainers,
  ResultAction,
  ResultActionMethods,
  ResultActionTypes,
  WindowNames,
} from '@cashier/cashier-model';
import {
  convertToUrl,
  eventBus,
  EventFactory,
  ProviderWindowService,
  TokenizerV1Service,
} from '@cashier/cashier-shared';

import {
  extractAmountValue,
  PaymentAttributesMapper,
  processFieldsWithSelectType,
} from '../services/common';
import {
  FormPayloadFactory,
  FormSchemaFactory,
} from '../services/confirmationForm';

const paymentErrorEventByType: Record<
  PaymentTypes,
  typeof EventFactory.depositError
> = {
  [PaymentTypes.Deposit]: EventFactory.depositError,
  [PaymentTypes.Withdrawal]: EventFactory.withdrawError,
};

export const paymentProcessingMiddleware: Middleware =
  (store) => (next) => async (action) => {
    const {
      paymentMethod: {
        paymentMethod: {
          vendor,
          id: paymentMethodId,
          referenceId,
          source,
          preprocessor,
          redirectContainer,
        },
        registrationRequired,
      },
      paymentForm: { formPayload, formSchema },
      cashierConfiguration: {
        merchantId,
        userId,
        sessionId,
        paymentType,
        language,
        host,
        currency,
        version,
      },
      customerInformation: { bonusCode },
    } = store.getState();

    const result = next(action);

    // TODO delete after testing 2914
    const isHostedFieldEnabled =
      getCookie(QaToolKitOptions.enableHostedField) === 'true';

    if (action.type === initiateProcessing.toString()) {
      const eventByType: { [key: string]: string } = {
        deposit: 'deposit_form_submit',
        withdrawal: 'withdraw_form_submit',
      };

      const paymentAttributes = PaymentAttributesMapper.fromPayload(
        formPayload as FormPayload,
      );

      eventBus.emit(
        EventFactory.paymentInitiated(
          eventByType[paymentType],
          (paymentAttributes.amount ||
            paymentAttributes.fiatAmount ||
            '0') as string,
          paymentMethodId,
          currency,
        ),
      );

      if (isHostedFieldEnabled) {
        if (preprocessor?.type === PreprocessorTypes.TOKENIZER_V1) {
          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.LoadTokenizerV1,
              isAccountUsed: !!formPayload.accountId,
            }),
          );
          return;
        }

        if (formSchema?.cardData) {
          eventBus.emit(
            EventFactory.cashierMessage({
              task: CashierMessages.GetEncryptedPayload,
            }),
          );
        } else {
          store.dispatch(continueProcessing({}));
        }
      } else {
        // TODO delete after testing 2914
        let preprocessedAttributes: Record<string, any> = {};

        const callbackParameterBag = {
          userId,
          sessionId,
          merchantId,
          language,
          host,
          paymentMethodId,
        };

        const finalAmount = extractAmountValue(formSchema, formPayload);

        store.dispatch(initiatePaymentSnapshot(finalAmount || ''));

        if (preprocessor?.type === PreprocessorTypes.TOKENIZER_V1) {
          try {
            const formPayload = selectPaymentFormPayload(store.getState());
            preprocessedAttributes =
              await TokenizerV1Service.getPreprocessedAttributes(
                formPayload,
                !!formPayload.accountId?.value,
                preprocessor,
              );
          } catch (error) {
            const transactionId =
              error && typeof error === 'object' && 'transactionId' in error
                ? (error.transactionId as string) || null
                : null;

            store.dispatch(
              processPaymentFailure({
                message: JSON.stringify(error),
                attributeErrorBag: null,
                errorDetails: { description: JSON.stringify(error) },
                transactionId,
                context: null,
              }),
            );
            return;
          }
        }

        if (registrationRequired) {
          store.dispatch(
            registerPayment({
              referenceId,
              type: paymentType,
              walletId: userId,
              sessionId,
              merchantId,
              methodId: paymentMethodId,
              attributes: {
                ...paymentAttributes,
                bonusCode: bonusCode.toString(),
                ...preprocessedAttributes,
              },
            }),
          );
        } else {
          store.dispatch(
            processPayment({
              paymentMethodId,
              vendor,
              merchantId,
              paymentType,
              userId,
              sessionId,
              paymentAttributes: {
                ...paymentAttributes,
                bonusCode,
                ...preprocessedAttributes,
              },
              callbackParameterBag,
              referenceId,
              source,
            }),
          );
        }
      }
    }

    if (action.type === processPaymentSuccess.toString()) {
      const result: PaymentActionResult = action.payload;
      const { transactionId, status, paymentAttributes, resultAction } = result;

      if (paymentAttributes && paymentAttributes.length > 0) {
        const fields = processFieldsWithSelectType(paymentAttributes);
        const schema = FormSchemaFactory.create(fields);
        const payload = FormPayloadFactory.fromFormSchema(schema);

        store.dispatch(fillConfirmationForm({ schema, payload }));

        if (preprocessor?.type === PreprocessorTypes.SDK_PROCESSOR_V1) {
          store.dispatch(initiateConfirmation());

          return;
        }

        store.dispatch(setActiveScreen(DynamicScreens.ConfirmationForm));
      } else {
        store.dispatch(
          fetchPaymentInformationSuccess({
            transactionId,
            state: status as PaymentStates,
          }),
        );

        if (!resultAction) {
          eventBus.emit(EventFactory.scrollTop());

          ProviderWindowService.close(WindowNames.Window);
        }
      }
    }

    if (action.type === processPaymentFailure.toString()) {
      const paymentErrorEvent =
        paymentErrorEventByType[paymentType as PaymentTypes];

      if (paymentErrorEvent) {
        const {
          errorDetails,
          message,
          transactionId,
          context,
        }: PaymentActionError = action.payload;

        const { description, code } = errorDetails || {};
        const amountField =
          formPayload.amount || formPayload.fiatAmount || null;

        eventBus.emit(
          paymentErrorEvent(
            amountField?.value ? (amountField.value as string) : '',
            currency,
            paymentMethodId,
            description || message || '',
            transactionId || context?.payment?.id || '',
            code || context?.payment?.reasonCode,
          ),
        );
      }
    }

    if (action.type === registerPaymentSuccess.toString()) {
      const resultAction: ResultAction = action.payload;

      store.dispatch(setActiveScreen(DynamicScreens.ProcessingResult));

      if (
        ResultActionMethods.GET === resultAction.method &&
        ResultActionTypes.REDIRECT === resultAction.type &&
        resultAction.url
      ) {
        if (version === 'v2') {
          ProviderWindowService.redirect(
            resultAction.url as string,
            redirectContainer === RedirectContainers.NEW_TAB,
          );
        } else {
          eventBus.emit(
            EventFactory.paymentProcessed(200, {
              redirectUrl: convertToUrl(
                resultAction.url as string,
                resultAction.parameters,
              ),
            }),
          );
          eventBus.emit(EventFactory.paymentAttributesChanged([]));
        }
      }

      if (ResultActionTypes.HTML === resultAction.type && resultAction.output) {
        if (version === 'v2') {
          ProviderWindowService.writeContent(
            WindowNames.Window,
            resultAction.output,
          );
        } else {
          eventBus.emit(
            EventFactory.paymentProcessed(200, {
              html: resultAction.output,
            }),
          );
          eventBus.emit(EventFactory.paymentAttributesChanged([]));
        }
      }

      if (resultAction.type === ResultActionTypes.DISPLAY) {
        store.dispatch(setActiveScreen(DynamicScreens.FinalScreen));
      }
    }

    if (
      [registerPaymentFailure.type, processPaymentFailure.type].includes(
        action.type,
      )
    ) {
      const {
        paymentMethod: {
          paymentMethod: { source },
        },
      } = store.getState();

      ProviderWindowService.close(WindowNames.Window);

      const { attributeErrorBag, context }: PaymentActionError = action.payload;

      if (context) {
        const { ui: uiData, payment: paymentData } = context;

        if (
          uiData.display !== 'screen' &&
          uiData.message &&
          paymentData?.reasonCode
        ) {
          eventBus.emit(EventFactory.showMessage(uiData.message));
        }

        if (uiData.display === 'field') {
          if (attributeErrorBag) {
            store.dispatch(enrichPayloadWithErrors(attributeErrorBag));
          }

          if (source === IntegrationSources.External) {
            store.dispatch(
              refetchCertainPaymentMethod({
                type: paymentType,
                id: paymentMethodId,
              }),
            );
          } else {
            store.dispatch(setPaymentFormStatus(FormStatuses.READY));
          }
        }
      }
    }

    return result;
  };
